import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { platformApi } from '../../api'
import type { FormDefinition, Group, Workflow } from '../../types'
import {
    collectFieldRefs, conditionFields, routingFields, userRefFields,
    type FormWithDefinition,
} from './model'
import type { ConditionField } from '../../components/ConditionEditor'

type Member = { id: string; name: string; email: string }

export interface AdminData {
    loading: boolean
    error: string
    forms: FormWithDefinition[]
    groups: Group[]
    workflows: Workflow[]
    conditionFields: ConditionField[]
    routingFields: ConditionField[]
    userRefFields: { key: string; label: string }[]
    /** Cached group members; call ensureMembers to populate. */
    members: Record<string, Member[] | undefined>
    ensureMembers: (ids: string[], force?: boolean) => void
    reloadGroups: () => Promise<void>
    reloadWorkflows: () => Promise<void>
    reloadAll: () => Promise<void>
    goTab: (tab: 'workflows' | 'groups' | 'routing') => void
}

const Ctx = createContext<AdminData | null>(null)

export function useAdmin(): AdminData {
    const v = useContext(Ctx)
    if (!v) throw new Error('useAdmin outside AdminProvider')
    return v
}

const msg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong')

export function AdminProvider({ goTab, children }: { goTab: AdminData['goTab']; children: ReactNode }) {
    const [forms, setForms] = useState<FormWithDefinition[]>([])
    const [groups, setGroups] = useState<Group[]>([])
    const [workflows, setWorkflows] = useState<Workflow[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [members, setMembers] = useState<Record<string, Member[] | undefined>>({})
    const requested = useRef(new Set<string>())

    const reloadGroups = useCallback(async () => { setGroups(await platformApi.listGroups()) }, [])
    const reloadWorkflows = useCallback(async () => { setWorkflows(await platformApi.listWorkflows()) }, [])

    const reloadAll = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const [g, w, f] = await Promise.all([platformApi.listGroups(), platformApi.listWorkflows(), platformApi.listForms()])
            setGroups(g)
            setWorkflows(w)
            const published = f.filter((x) => x.status === 'published' || x.active_version)
            const withDefs = await Promise.all(published.map(async (form): Promise<FormWithDefinition> => {
                try {
                    const r = await platformApi.getForm(form.slug)
                    return { form, definition: (r.version?.definition as FormDefinition | undefined) ?? null }
                } catch {
                    return { form, definition: null }
                }
            }))
            setForms(withDefs)
        } catch (e) {
            setError(msg(e))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { void reloadAll() }, [reloadAll])

    const ensureMembers = useCallback((ids: string[], force = false) => {
        for (const id of ids) {
            if (!force && requested.current.has(id)) continue
            requested.current.add(id)
            platformApi.getGroup(id)
                .then((g) => setMembers((m) => ({ ...m, [id]: g.members ?? [] })))
                .catch(() => setMembers((m) => ({ ...m, [id]: [] })))
        }
    }, [])

    const value = useMemo<AdminData>(() => {
        const refs = collectFieldRefs(forms)
        return {
            loading, error, forms, groups, workflows, members, ensureMembers,
            conditionFields: conditionFields(refs),
            routingFields: routingFields(forms),
            userRefFields: userRefFields(refs),
            reloadGroups, reloadWorkflows, reloadAll, goTab,
        }
    }, [loading, error, forms, groups, workflows, members, ensureMembers, reloadGroups, reloadWorkflows, reloadAll, goTab])

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
